import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './utils/authUtils';
import { nacmeLogo } from './assets/images';

const BACKEND_URL = API_URL;

const PartnerActivation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Activation token is missing from the URL.');
      setLoading(false);
      return;
    }

    // Validate the activation token with the backend
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/partner/validate-activation-token`, {
        token: token
      });

      if (response.data.valid) {
        setPartnerInfo(response.data.partner);
        // Pre-fill username if available
        if (response.data.partner.email) {
          setFormData(prev => ({
            ...prev,
            username: response.data.partner.email
          }));
        }
        setLoading(false);
      } else {
        setError(response.data.message || 'Invalid or expired activation token.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error validating token:', err);
      setError(err.response?.data?.detail || 'Failed to validate activation token.');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (!formData.username.trim()) {
      setError('Username is required.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post(`${BACKEND_URL}/api/partner/activate-account`, {
        token: token,
        username: formData.username.trim(),
        password: formData.password
      });

      if (response.data.success) {
        setSuccess(true);
        // Redirect to login page after a delay
        setTimeout(() => {
          navigate('/partner/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Failed to activate account.');
      }
    } catch (err) {
      console.error('Error activating account:', err);
      setError(err.response?.data?.detail || 'Failed to activate account.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating activation token...</p>
        </div>
      </div>
    );
  }

  if (error && !partnerInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="w-full max-w-md p-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center mb-6">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-16 w-auto mx-auto mb-4 drop-shadow"
              />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Account Activation
              </h1>
            </div>
            
            <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
            
            <div className="text-center">
              <button
                onClick={() => navigate('/partner/login')}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Go to Partner Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="w-full max-w-md p-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center mb-6">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-16 w-auto mx-auto mb-4 drop-shadow"
              />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Account Activated!
              </h1>
            </div>
            
            <div className="bg-green-100 border border-green-300 text-green-700 px-4 py-3 rounded mb-4">
              Your account has been successfully activated. You can now log in with your new credentials.
            </div>
            
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Redirecting to login page in a few seconds...
              </p>
              <button
                onClick={() => navigate('/partner/login')}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Go to Login Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
      <div className="w-full max-w-md p-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center mb-6">
            <img
              src={nacmeLogo}
              alt="NACME Logo"
              className="h-16 w-auto mx-auto mb-4 drop-shadow"
            />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Activate Your Account
            </h1>
            <p className="text-gray-600">
              Welcome, {partnerInfo?.full_name}
            </p>
            <p className="text-sm text-gray-500">
              {partnerInfo?.university}
            </p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your username"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will be used to log in to your account
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Create Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength="8"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
              />
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                minLength="8"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
              }`}
            >
              {loading ? 'Activating Account...' : 'Activate Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/partner/login')}
              className="text-blue-600 hover:text-blue-800 underline text-sm"
            >
              Already have an account? Login here
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerActivation;